/**
 * Tax & Fee Scheme Management Page.
 *
 * Admin page for managing tax and fee rules with conditional auto-application,
 * certificate requirements, priority ordering, and interstate/multi-jurisdiction
 * handling. Each scheme card shows conditions, certificate requirements, and
 * a quick summary of when the rule fires.
 */

import { useState, useMemo } from "react";
import {
  Receipt,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Shield,
  MapPin,
  Tag,
  Layers,
  Star,
  AlertTriangle,
  Pencil,
  Calculator,
  Check,
  X,
  Play,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";
import ActionDrawer from "@/components/ui/ActionDrawer";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import MultiSelect from "@/components/ui/MultiSelect";
import type { TaxScheme } from "@/types/sales-order";

const SCHEME_TYPES = [
  { value: "percentage", label: "Percentage of subtotal" },
  { value: "flat_per_unit", label: "Flat $ per unit" },
  { value: "flat_per_order", label: "Flat $ per order" },
];

const APPLIES_TO = [
  { value: "sales", label: "Sales orders" },
  { value: "purchase", label: "Purchase orders" },
  { value: "both", label: "Both" },
];

const APPLY_LEVELS = [
  { value: "order", label: "Order total (Tax)" },
  { value: "line", label: "Line item (Fees)" },
];

const DEFAULT_CERT_TYPES = [
  { value: "ag_exemption", label: "Agricultural Exemption" },
  { value: "resale_cert", label: "Resale Certificate" },
  { value: "interstate_form", label: "Interstate/Multijurisdiction Form" },
  { value: "government", label: "Government Exemption" },
];

const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];

interface SchemeFormData {
  name: string;
  description: string;
  scheme_type: string;
  rate: string;
  flat_amount: string;
  applies_to: string;
  apply_level: "line" | "order";
  is_default: boolean;
  priority: string;
  condition_customer_tags: string[];
  condition_asset_categories: string[];
  condition_interstate: boolean | null;
  condition_same_state: boolean | null;
  condition_states: string[];
  requires_certificate: boolean;
  certificate_type: string;
  certificate_must_be_on_file: boolean;
  certificate_per_order: boolean;
  certificate_renewal_months: string;
}

const emptyForm: SchemeFormData = {
  name: "",
  description: "",
  scheme_type: "percentage",
  rate: "",
  flat_amount: "",
  applies_to: "sales",
  apply_level: "order",
  is_default: false,
  priority: "100",
  condition_customer_tags: [],
  condition_asset_categories: [],
  condition_interstate: null,
  condition_same_state: null,
  condition_states: [],
  requires_certificate: false,
  certificate_type: "",
  certificate_must_be_on_file: false,
  certificate_per_order: false,
  certificate_renewal_months: "0",
};

function schemeToForm(s: TaxScheme): SchemeFormData {
  return {
    name: s.name,
    description: s.description || "",
    scheme_type: s.scheme_type,
    rate:
      s.scheme_type === "percentage"
        ? String(Number(s.rate) * 100)
        : String(s.rate),
    flat_amount: String(s.flat_amount),
    applies_to: s.applies_to,
    apply_level: s.apply_level ?? "order",
    is_default: s.is_default,
    priority: String(s.priority),
    condition_customer_tags: s.condition_customer_tags || [],
    condition_asset_categories: s.condition_asset_categories || [],
    condition_interstate: s.condition_interstate,
    condition_same_state: s.condition_same_state,
    condition_states: s.condition_states || [],
    requires_certificate: s.requires_certificate,
    certificate_type: s.certificate_type || "",
    certificate_must_be_on_file: s.certificate_must_be_on_file,
    certificate_per_order: s.certificate_per_order,
    certificate_renewal_months: String(s.certificate_renewal_months),
  };
}

function formToPayload(f: SchemeFormData) {
  const rateVal =
    f.scheme_type === "percentage"
      ? parseFloat(f.rate || "0") / 100
      : parseFloat(f.rate || "0");
  return {
    name: f.name,
    description: f.description || undefined,
    scheme_type: f.scheme_type,
    rate: rateVal,
    flat_amount: parseFloat(f.flat_amount || "0"),
    applies_to: f.applies_to,
    apply_level: f.apply_level,
    is_default: f.is_default,
    priority: parseInt(f.priority || "100", 10),
    condition_customer_tags: f.condition_customer_tags.length
      ? f.condition_customer_tags
      : null,
    condition_asset_categories: f.condition_asset_categories.length
      ? f.condition_asset_categories
      : null,
    condition_interstate: f.condition_interstate,
    condition_same_state: f.condition_same_state,
    condition_states: f.condition_states.length ? f.condition_states : null,
    requires_certificate: f.requires_certificate,
    certificate_type: f.requires_certificate ? f.certificate_type : null,
    certificate_must_be_on_file: f.certificate_must_be_on_file,
    certificate_per_order: f.certificate_per_order,
    certificate_renewal_months: parseInt(
      f.certificate_renewal_months || "0",
      10,
    ),
  };
}

function TaxSchemesPage() {
  const qc = useQueryClient();
  const { data: schemes, isLoading } = useQuery<TaxScheme[]>({
    queryKey: ["tax-schemes"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/tax-schemes")).data as TaxScheme[],
  });

  interface ConditionOptions {
    asset_categories: { value: string; label: string }[];
    customer_tags: string[];
  }

  const { data: conditionOptions } = useQuery<ConditionOptions>({
    queryKey: ["tax-schemes", "condition-options"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/tax-schemes/condition-options"))
        .data as ConditionOptions,
    staleTime: 5 * 60 * 1000,
  });

  const categoryOptions = useMemo(
    () => conditionOptions?.asset_categories ?? [],
    [conditionOptions],
  );
  const tagOptions = useMemo(
    () =>
      (conditionOptions?.customer_tags ?? []).map((t) => ({
        value: t,
        label: t,
      })),
    [conditionOptions],
  );

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      (await apiClient.post("/api/v1/tax-schemes", data)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tax-schemes"] });
      toast.success("Tax scheme created");
    },
    onError: () => toast.error("Failed to create tax scheme"),
  });
  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Record<string, unknown>;
    }) => (await apiClient.put(`/api/v1/tax-schemes/${id}`, data)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tax-schemes"] });
      toast.success("Tax scheme updated");
    },
    onError: () => toast.error("Failed to update tax scheme"),
  });
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/v1/tax-schemes/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tax-schemes"] });
      toast.success("Tax scheme deleted");
    },
    onError: () => toast.error("Failed to delete tax scheme"),
  });

  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<SchemeFormData>({ ...emptyForm });
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showStates, setShowStates] = useState(false);
  const [simDrawerOpen, setSimDrawerOpen] = useState(false);
  const [customCertTypes, setCustomCertTypes] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [customCertInput, setCustomCertInput] = useState("");
  const [showCustomCertInput, setShowCustomCertInput] = useState(false);

  // Build combined cert type list: defaults + any custom-added + any types from existing schemes.
  const allCertTypes = (() => {
    const combined = [...DEFAULT_CERT_TYPES, ...customCertTypes];
    const knownValues = new Set(combined.map((c) => c.value));
    // Add cert types from existing schemes that aren't in the defaults.
    if (schemes) {
      for (const s of schemes) {
        if (s.certificate_type && !knownValues.has(s.certificate_type)) {
          combined.push({
            value: s.certificate_type,
            label: s.certificate_type,
          });
          knownValues.add(s.certificate_type);
        }
      }
    }
    return combined;
  })();

  const setField = <K extends keyof SchemeFormData>(
    key: K,
    value: SchemeFormData[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    await createMutation.mutateAsync(formToPayload(form));
    setShowCreate(false);
    setForm({ ...emptyForm });
    setShowStates(false);
    setShowCustomCertInput(false);
    setCustomCertInput("");
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId) return;
    await updateMutation.mutateAsync({
      id: editingId,
      data: formToPayload(form),
    });
    setEditingId(null);
    setForm({ ...emptyForm });
  };

  const startEdit = (s: TaxScheme) => {
    setEditingId(s.id);
    setForm(schemeToForm(s));
    setShowCreate(false);
    setShowStates((s.condition_states?.length ?? 0) > 0);
    setShowCustomCertInput(false);
    setCustomCertInput("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setShowStates(false);
    setShowCustomCertInput(false);
    setCustomCertInput("");
  };

  const fmt = (v: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(v);

  const selectClass =
    "mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)] transition-all duration-200 hover:border-[var(--border-strong)] focus:outline-none focus-visible:shadow-[var(--focus-ring)] focus-visible:border-[var(--border-focus)]";

  const FormSectionHeader = ({
    icon: Icon,
    title,
    hint,
  }: {
    icon: React.ElementType;
    title: string;
    hint?: string;
  }) => (
    <div className="flex items-center justify-between pb-2 mb-3 border-b border-[var(--border-subtle)]">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-[var(--text-tertiary)]" />
        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
          {title}
        </span>
      </div>
      {hint && (
        <span className="text-[10px] text-[var(--text-tertiary)]">{hint}</span>
      )}
    </div>
  );

  const renderFormContent = (
    onSubmit: (e: React.FormEvent) => void,
    isEdit: boolean,
  ) => (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* ── Section 1: Basics ── */}
      <div>
        <FormSectionHeader icon={Receipt} title="Scheme Details" />
        <div className="space-y-3">
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            required
            placeholder='e.g., "MS ATV Registration Fee"'
          />
          <div className="grid grid-cols-[5fr_3fr_4fr] gap-3 rounded-[var(--radius-md)] bg-[var(--surface-sunken)] p-3">
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                Type
              </label>
              <select
                value={form.scheme_type}
                onChange={(e) => setField("scheme_type", e.target.value)}
                className={selectClass}
              >
                {SCHEME_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            {form.scheme_type === "percentage" ? (
              <Input
                label="Rate (%)"
                type="number"
                step="0.01"
                min="0"
                value={form.rate}
                onChange={(e) => setField("rate", e.target.value)}
                required
                placeholder="7.25"
              />
            ) : (
              <Input
                label="Amount ($)"
                type="number"
                step="0.01"
                min="0"
                value={form.flat_amount}
                onChange={(e) => setField("flat_amount", e.target.value)}
                required
                placeholder="150.00"
              />
            )}
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                Applies To
              </label>
              <select
                value={form.applies_to}
                onChange={(e) => setField("applies_to", e.target.value)}
                className={selectClass}
              >
                {APPLIES_TO.map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="rounded-[var(--radius-md)] bg-[var(--surface-sunken)] p-3">
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
              Apply at
            </label>
            <select
              value={form.apply_level}
              onChange={(e) =>
                setField("apply_level", e.target.value as "line" | "order")
              }
              className={selectClass}
            >
              {APPLY_LEVELS.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-[var(--text-tertiary)] mt-1">
              {form.apply_level === "line"
                ? "Charged per line item and shown as “Fees” in order totals — good for per-unit or category-specific fees."
                : "Applied to the whole order subtotal and shown as “Tax” in order totals — good for sales/use tax."}
            </p>
          </div>
          <div>
            <Input
              label="Description"
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              placeholder="Optional — explains when this applies"
            />
            <p className="text-[11px] text-[var(--text-tertiary)] mt-1">
              Shown on invoices and order summaries
            </p>
          </div>

          {/* Priority + Default — only relevant for percentage schemes */}
          {form.scheme_type === "percentage" && (
            <>
              <div className="flex items-start gap-4 rounded-[var(--radius-md)] bg-[var(--surface-sunken)] p-3">
                <div className="w-28 shrink-0">
                  <Input
                    label="Priority"
                    type="number"
                    min="0"
                    value={form.priority}
                    onChange={(e) => setField("priority", e.target.value)}
                    placeholder="100"
                  />
                  <p className="text-[11px] text-[var(--text-tertiary)] mt-1">
                    Lower = evaluated first
                  </p>
                </div>
                <label className="flex items-center gap-2.5 text-sm text-[var(--text-secondary)] cursor-pointer select-none pt-6">
                  <input
                    type="checkbox"
                    checked={form.is_default}
                    onChange={(e) => setField("is_default", e.target.checked)}
                    className="h-4 w-4 rounded-[var(--radius-sm)] border-[var(--border-default)] accent-[var(--color-interactive-primary)]"
                  />
                  Default fallback when no other rate matches
                </label>
              </div>
              <p className="text-[11px] text-[var(--text-tertiary)] mt-1">
                Percentage schemes use first-match-wins — only the
                highest-priority matching rule applies. Flat fees always stack
                regardless of priority.
              </p>
            </>
          )}
        </div>
      </div>

      {/* ── Section 2: Conditions ── */}
      <div>
        <FormSectionHeader
          icon={Layers}
          title="Conditions"
          hint="All conditions must match for auto-apply"
        />
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <MultiSelect
                label="Asset Categories"
                options={categoryOptions}
                value={form.condition_asset_categories}
                onChange={(vals) =>
                  setField("condition_asset_categories", vals)
                }
                placeholder="Select categories..."
                creatable
              />
              <p className="text-[11px] text-[var(--text-tertiary)] mt-1">
                Only assets in these categories will trigger this scheme
              </p>
            </div>
            <div>
              <MultiSelect
                label="Customer Tags"
                options={tagOptions}
                value={form.condition_customer_tags}
                onChange={(vals) => setField("condition_customer_tags", vals)}
                placeholder="Select tags..."
                creatable
              />
              <p className="text-[11px] text-[var(--text-tertiary)] mt-1">
                Customer must have ALL listed tags for this scheme to match
              </p>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
              Interstate Rule
            </label>
            <select
              value={
                form.condition_interstate === true
                  ? "interstate"
                  : form.condition_same_state === true
                    ? "same_state"
                    : "any"
              }
              onChange={(e) => {
                const v = e.target.value;
                setField(
                  "condition_interstate",
                  v === "interstate" ? true : null,
                );
                setField(
                  "condition_same_state",
                  v === "same_state" ? true : null,
                );
              }}
              className={selectClass}
            >
              <option value="any">Any (no restriction)</option>
              <option value="interstate">Interstate only</option>
              <option value="same_state">Same state only</option>
            </select>
          </div>
          <label className="flex items-center gap-2.5 text-sm text-[var(--text-secondary)] cursor-pointer select-none rounded-[var(--radius-md)] p-2.5 -mx-0.5 hover:bg-[var(--surface-sunken)] transition-colors">
            <input
              type="checkbox"
              checked={showStates}
              onChange={(e) => {
                setShowStates(e.target.checked);
                if (!e.target.checked) setField("condition_states", []);
              }}
              className="h-4 w-4 rounded-[var(--radius-sm)] border-[var(--border-default)] accent-[var(--color-interactive-primary)]"
            />
            <MapPin className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />
            Restrict to specific states
          </label>
          {showStates && (
            <div className="rounded-[var(--radius-md)] bg-[var(--surface-sunken)] p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setField("condition_states", [...US_STATES])}
                    className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                  >
                    Select All
                  </button>
                  <span className="text-[var(--text-tertiary)]">|</span>
                  <button
                    type="button"
                    onClick={() => setField("condition_states", [])}
                    className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                  >
                    Clear All
                  </button>
                </div>
                {form.condition_states.length > 0 && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-brand-200 text-brand-700 dark:bg-brand-900/50 dark:text-brand-300">
                    {form.condition_states.length} selected
                  </span>
                )}
              </div>
              <div className="grid grid-cols-10 gap-1">
                {US_STATES.map((st) => (
                  <button
                    key={st}
                    type="button"
                    onClick={() =>
                      setField(
                        "condition_states",
                        form.condition_states.includes(st)
                          ? form.condition_states.filter((s) => s !== st)
                          : [...form.condition_states, st],
                      )
                    }
                    className={`px-1.5 py-1 text-[11px] font-medium rounded-[var(--radius-sm)] border transition-all duration-150 ${
                      form.condition_states.includes(st)
                        ? "bg-brand-200 border-brand-300 text-brand-700 shadow-sm dark:bg-brand-900/50 dark:border-brand-700 dark:text-brand-300"
                        : "border-transparent text-[var(--text-tertiary)] hover:bg-[var(--surface-base)] hover:text-[var(--text-secondary)]"
                    }`}
                  >
                    {st}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Section 3: Certificate Requirements ── */}
      <div>
        <FormSectionHeader icon={Shield} title="Certificate Requirements" />
        <div className="space-y-3">
          <label className="flex items-center gap-2.5 text-sm text-[var(--text-secondary)] cursor-pointer select-none rounded-[var(--radius-md)] p-2.5 -mx-0.5 hover:bg-[var(--surface-sunken)] transition-colors">
            <input
              type="checkbox"
              checked={form.requires_certificate}
              onChange={(e) =>
                setField("requires_certificate", e.target.checked)
              }
              className="h-4 w-4 rounded-[var(--radius-sm)] border-[var(--border-default)] accent-[var(--color-interactive-primary)]"
            />
            Requires a certificate to apply this rate
          </label>
          {form.requires_certificate && (
            <div className="rounded-[var(--radius-md)] bg-[var(--surface-sunken)] p-4 space-y-4 border-l-2 border-[var(--color-interactive-primary)]/30">
              <div>
                <label className="block text-[13px] font-medium text-[var(--text-primary)]">
                  Certificate Type
                </label>
                <select
                  value={
                    allCertTypes.some((c) => c.value === form.certificate_type)
                      ? form.certificate_type
                      : "__custom__"
                  }
                  onChange={(e) => {
                    if (e.target.value === "__add__") {
                      setShowCustomCertInput(true);
                    } else if (e.target.value === "__custom__") {
                      // No-op
                    } else {
                      setField("certificate_type", e.target.value);
                      setShowCustomCertInput(false);
                    }
                  }}
                  className={selectClass}
                >
                  <option value="">Select type...</option>
                  {allCertTypes.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                  {form.certificate_type &&
                    !allCertTypes.some(
                      (c) => c.value === form.certificate_type,
                    ) && (
                      <option value="__custom__">
                        {form.certificate_type}
                      </option>
                    )}
                  <option value="__add__">+ Add custom type...</option>
                </select>
                {showCustomCertInput && (
                  <div className="mt-2 flex gap-2 animate-fade-in">
                    <input
                      type="text"
                      value={customCertInput}
                      onChange={(e) => setCustomCertInput(e.target.value)}
                      placeholder="Enter certificate type name"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          if (customCertInput.trim()) {
                            const val = customCertInput
                              .trim()
                              .toLowerCase()
                              .replace(/\s+/g, "_");
                            const label = customCertInput.trim();
                            if (!allCertTypes.some((c) => c.value === val)) {
                              setCustomCertTypes((prev) => [
                                ...prev,
                                { value: val, label },
                              ]);
                            }
                            setField("certificate_type", val);
                            setCustomCertInput("");
                            setShowCustomCertInput(false);
                          }
                        }
                      }}
                      className="block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)] focus-visible:border-[var(--border-focus)] transition-all duration-200"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (customCertInput.trim()) {
                          const val = customCertInput
                            .trim()
                            .toLowerCase()
                            .replace(/\s+/g, "_");
                          const label = customCertInput.trim();
                          if (!allCertTypes.some((c) => c.value === val)) {
                            setCustomCertTypes((prev) => [
                              ...prev,
                              { value: val, label },
                            ]);
                          }
                          setField("certificate_type", val);
                          setCustomCertInput("");
                          setShowCustomCertInput(false);
                        }
                      }}
                      className="shrink-0 h-[38px] px-4 rounded-[var(--radius-md)] bg-[var(--color-interactive-primary)] text-[var(--text-inverse)] text-xs font-semibold shadow-sm hover:shadow-md hover:-translate-y-px active:scale-[0.97] transition-all duration-200"
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCustomCertInput(false);
                        setCustomCertInput("");
                      }}
                      className="shrink-0 h-[38px] px-3 rounded-[var(--radius-md)] text-xs text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-base)] transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2.5 text-sm text-[var(--text-secondary)] cursor-pointer select-none rounded-[var(--radius-md)] bg-[var(--surface-base)] p-3 border border-[var(--border-subtle)] hover:border-[var(--border-default)] transition-colors">
                  <input
                    type="checkbox"
                    checked={form.certificate_must_be_on_file}
                    onChange={(e) =>
                      setField("certificate_must_be_on_file", e.target.checked)
                    }
                    className="h-4 w-4 rounded-[var(--radius-sm)] border-[var(--border-default)] accent-[var(--color-interactive-primary)]"
                  />
                  Must be on file
                </label>
                <label className="flex items-center gap-2.5 text-sm text-[var(--text-secondary)] cursor-pointer select-none rounded-[var(--radius-md)] bg-[var(--surface-base)] p-3 border border-[var(--border-subtle)] hover:border-[var(--border-default)] transition-colors">
                  <input
                    type="checkbox"
                    checked={form.certificate_per_order}
                    onChange={(e) =>
                      setField("certificate_per_order", e.target.checked)
                    }
                    className="h-4 w-4 rounded-[var(--radius-sm)] border-[var(--border-default)] accent-[var(--color-interactive-primary)]"
                  />
                  Required per order
                </label>
              </div>
              <div>
                <Input
                  label="Renewal (months)"
                  type="number"
                  min="0"
                  value={form.certificate_renewal_months}
                  onChange={(e) =>
                    setField("certificate_renewal_months", e.target.value)
                  }
                  placeholder="0 = never"
                />
                <p className="text-[11px] text-[var(--text-tertiary)] mt-1">
                  Set to 0 for certificates that never expire
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t border-[var(--border-subtle)]">
        <Button
          type="submit"
          isLoading={
            isEdit ? updateMutation.isPending : createMutation.isPending
          }
        >
          {isEdit ? "Save Changes" : "Create Scheme"}
        </Button>
      </div>
    </form>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">
            Tax & Fee Schemes
          </h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Configure rules that auto-apply to orders based on customer,
            category, and location.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setSimDrawerOpen(true)}>
            <Calculator className="h-4 w-4" /> Tax Simulator
          </Button>
          <Button
            onClick={() => {
              setShowCreate(true);
              setEditingId(null);
              setForm({ ...emptyForm });
              setShowStates(false);
              setShowCustomCertInput(false);
              setCustomCertInput("");
            }}
          >
            <Plus className="h-4 w-4" /> Add Scheme
          </Button>
        </div>
      </div>

      {/* Create Drawer */}
      <ActionDrawer
        isOpen={showCreate && !editingId}
        onClose={() => setShowCreate(false)}
        title="New Tax Scheme"
        description="Configure a tax or fee rule with conditions and certificate requirements."
        icon={Receipt}
        size="lg"
      >
        {renderFormContent(handleCreate, false)}
      </ActionDrawer>

      {/* Edit Drawer */}
      <ActionDrawer
        isOpen={!!editingId}
        onClose={cancelEdit}
        title="Edit Tax Scheme"
        description="Update this tax or fee rule."
        icon={Pencil}
        size="lg"
      >
        {renderFormContent(handleUpdate, true)}
      </ActionDrawer>

      {isLoading ? (
        <LoadingSpinner className="py-12" />
      ) : schemes && schemes.length > 0 ? (
        <div className="space-y-2 stagger-children">
          {schemes.map((s) => {
            const isExpanded = expandedId === s.id;
            const hasConditions =
              (s.condition_customer_tags?.length ?? 0) > 0 ||
              (s.condition_asset_categories?.length ?? 0) > 0 ||
              s.condition_interstate != null ||
              s.condition_same_state != null ||
              (s.condition_states?.length ?? 0) > 0;

            return (
              <Card key={s.id} padding="sm" className="hover-lift">
                <div className="flex items-center gap-4">
                  {/* Icon */}
                  <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                      s.scheme_type === "percentage"
                        ? "bg-[var(--fill-info)] text-[var(--text-info)]"
                        : "bg-[var(--fill-warning)] text-[var(--text-warning)]"
                    }`}
                  >
                    {s.scheme_type === "percentage" ? "%" : "$"}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-[var(--text-primary)]">
                        {s.name}
                      </span>
                      {s.is_default && (
                        <Badge variant="warning">
                          <Star className="h-3 w-3 mr-0.5" />
                          Default
                        </Badge>
                      )}
                      <Badge
                        variant={
                          s.scheme_type === "percentage" ? "info" : "default"
                        }
                      >
                        {SCHEME_TYPES.find((t) => t.value === s.scheme_type)
                          ?.label || s.scheme_type}
                      </Badge>
                      <Badge variant={s.is_active ? "success" : "default"}>
                        {s.is_active ? "Active" : "Inactive"}
                      </Badge>
                      {s.requires_certificate && (
                        <Badge variant="warning">
                          <Shield className="h-3 w-3 mr-0.5" />
                          Certificate
                        </Badge>
                      )}
                      {hasConditions && (
                        <Badge variant="info">
                          <Layers className="h-3 w-3 mr-0.5" />
                          Conditional
                        </Badge>
                      )}
                    </div>
                    {s.description && (
                      <p className="mt-0.5 text-xs text-[var(--text-tertiary)] truncate">
                        {s.description}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {s.condition_customer_tags?.map((t) => (
                        <span
                          key={t}
                          className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300"
                        >
                          <Tag className="h-2.5 w-2.5" />
                          {t}
                        </span>
                      ))}
                      {s.condition_asset_categories?.map((c) => (
                        <span
                          key={c}
                          className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300"
                        >
                          {c}
                        </span>
                      ))}
                      {s.condition_interstate && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                          <MapPin className="h-2.5 w-2.5" />
                          Interstate
                        </span>
                      )}
                      {s.condition_same_state && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300">
                          <MapPin className="h-2.5 w-2.5" />
                          Same State
                        </span>
                      )}
                      {s.condition_states && s.condition_states.length > 0 && (
                        <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                          States: {s.condition_states.join(", ")}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Rate/Amount display */}
                  <div className="shrink-0 text-right mr-2">
                    <p className="text-lg font-bold text-brand-600">
                      {s.scheme_type === "percentage"
                        ? `${(Number(s.rate) * 100).toFixed(2)}%`
                        : fmt(Number(s.flat_amount))}
                    </p>
                    <p className="text-[10px] text-[var(--text-tertiary)]">
                      Priority: {s.priority}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => startEdit(s)}
                      title="Edit"
                      className="rounded-[var(--radius-lg)] p-2 text-[var(--text-secondary)] transition-colors hover:bg-[var(--color-surface-secondary)] hover:text-[var(--text-primary)]"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : s.id)}
                      title="Details"
                      className="rounded-[var(--radius-lg)] p-2 text-[var(--text-secondary)] transition-colors hover:bg-[var(--color-surface-secondary)] hover:text-[var(--text-primary)]"
                    >
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      onClick={() => {
                        if (confirm("Delete this tax scheme?"))
                          deleteMutation.mutate(s.id);
                      }}
                      className="rounded-[var(--radius-lg)] p-2 text-[var(--text-danger)] transition-colors hover:bg-[var(--fill-danger)]"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="mt-3 pt-3 border-t border-[var(--color-border)] space-y-2 text-xs text-[var(--text-secondary)]">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div>
                        <span className="font-medium text-[var(--text-primary)]">
                          Applies to:
                        </span>{" "}
                        {s.applies_to}
                      </div>
                      <div>
                        <span className="font-medium text-[var(--text-primary)]">
                          Apply at:
                        </span>{" "}
                        {(s.apply_level ?? "order") === "line"
                          ? "Line item (Fees)"
                          : "Order total (Tax)"}
                      </div>
                      <div>
                        <span className="font-medium text-[var(--text-primary)]">
                          Priority:
                        </span>{" "}
                        {s.priority}
                      </div>
                      <div>
                        <span className="font-medium text-[var(--text-primary)]">
                          Rate:
                        </span>{" "}
                        {Number(s.rate) > 0
                          ? `${(Number(s.rate) * 100).toFixed(4)}%`
                          : "N/A"}
                      </div>
                      <div>
                        <span className="font-medium text-[var(--text-primary)]">
                          Flat amount:
                        </span>{" "}
                        {Number(s.flat_amount) > 0
                          ? fmt(Number(s.flat_amount))
                          : "N/A"}
                      </div>
                    </div>
                    {s.requires_certificate && (
                      <div className="flex items-center gap-2 text-amber-600 dark:text-amber-300">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        Requires{" "}
                        <strong>
                          {allCertTypes.find(
                            (c) => c.value === s.certificate_type,
                          )?.label || s.certificate_type}
                        </strong>
                        {s.certificate_must_be_on_file && " — must be on file"}
                        {s.certificate_per_order && " — required per order"}
                        {s.certificate_renewal_months > 0 &&
                          ` — renews every ${s.certificate_renewal_months} months`}
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <div className="flex flex-col items-center py-12">
            <div className="empty-state-icon">
              <Receipt />
            </div>
            <h3 className="mt-4 text-lg font-medium text-[var(--text-primary)]">
              No tax/fee schemes
            </h3>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Add a scheme to auto-apply taxes and fees on sales orders.
            </p>
          </div>
        </Card>
      )}

      {/* ── Tax Simulator ── */}
      <TaxSimulator
        isOpen={simDrawerOpen}
        onClose={() => setSimDrawerOpen(false)}
        categoryOptions={categoryOptions}
        tagOptions={tagOptions}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tax Simulator
// ---------------------------------------------------------------------------

interface SchemeEvaluationTrace {
  scheme_id: string;
  scheme_name: string;
  scheme_type: string;
  apply_level: string;
  /** Which total this row lands in. A fee is not tax and must not be shown as one. */
  charge_class: "tax" | "fee";
  is_taxable: boolean;
  bucket: "tax" | "fee";
  rate: number | null;
  flat_amount: number | null;
  priority: number;
  matched: boolean;
  skip_reason: string | null;
  applied_amount: number | null;
  /** What this scheme charged against, so the arithmetic can be followed. */
  base_used: number | null;
}

interface SimulationContext {
  org_state: string | null;
  customer_state: string | null;
  is_interstate: boolean;
  counterparty_tags: string[];
  asset_categories: string[];
  is_tax_exempt: boolean;
}

/**
 * How the taxable base was arrived at.
 *
 * The point of the simulator is explaining a total, not just quoting one, so the
 * server returns the decomposition rather than leaving the UI to guess at it.
 */
interface BaseBreakdown {
  subtotal: number;
  discount_amount: number;
  discounted_subtotal: number;
  taxable_charges: number;
  non_taxable_charges: number;
  taxable_fees: number;
  non_taxable_fees: number;
  taxable_base: number;
}

interface SimulationResult {
  total_tax: number;
  total_fees: number;
  applied_taxes: Array<{
    scheme_name: string;
    scheme_type: string;
    rate: number;
    flat_amount: number;
    calculated_amount: number;
    charge_class: "tax" | "fee";
    certificate_verified: boolean;
    warnings: string[];
  }>;
  warnings: string[];
  evaluation_trace: SchemeEvaluationTrace[];
  context: SimulationContext;
  base: BaseBreakdown | null;
  /** Discounted subtotal + charges + fees + tax. Every dollar, not just the tax. */
  grand_total: number;
}

interface SimFormData {
  entity_type: string;
  subtotal: string;
  customer_state: string;
  customer_tags: string[];
  asset_categories: string[];
  cert_document_provided: boolean;
  cert_document_current: boolean;
  /** An order-level discount, which reduces what tax and percentage fees charge on. */
  discount_type: "" | "percent" | "amount";
  discount_value: string;
  /** Line charges, split by whether they join the taxable base. */
  taxable_charges: string;
  non_taxable_charges: string;
  /** Units, so a flat_per_unit scheme simulates the real multiple. */
  quantity: string;
}

const SIM_EMPTY: SimFormData = {
  entity_type: "sales_order",
  subtotal: "",
  customer_state: "",
  customer_tags: [],
  asset_categories: [],
  cert_document_provided: false,
  cert_document_current: true,
  discount_type: "",
  discount_value: "",
  taxable_charges: "",
  non_taxable_charges: "",
  quantity: "1",
};

const fmtCurrency = (v: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(v);

/** One line of the simulator's totals stack. */
function SimTotalRow({
  label,
  value,
  muted = false,
}: {
  label: string;
  value: number;
  muted?: boolean;
}) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span
        className={
          muted ? "text-[var(--text-tertiary)]" : "text-[var(--text-secondary)]"
        }
      >
        {label}
      </span>
      <span
        className={`tabular-nums ${muted ? "text-[var(--text-tertiary)]" : "text-[var(--text-primary)]"}`}
      >
        {fmtCurrency(value)}
      </span>
    </div>
  );
}

function TaxSimulator({
  isOpen,
  onClose,
  categoryOptions,
  tagOptions,
}: {
  isOpen: boolean;
  onClose: () => void;
  categoryOptions: { value: string; label: string }[];
  tagOptions: { value: string; label: string }[];
}) {
  const [form, setForm] = useState<SimFormData>({ ...SIM_EMPTY });
  const [result, setResult] = useState<SimulationResult | null>(null);

  const setSimField = <K extends keyof SimFormData>(
    key: K,
    value: SimFormData[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const simulateMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        entity_type: form.entity_type,
        subtotal: parseFloat(form.subtotal || "0"),
        customer_state: form.customer_state || null,
        customer_tags: form.customer_tags.length ? form.customer_tags : null,
        asset_categories: form.asset_categories.length
          ? form.asset_categories
          : null,
        cert_document_provided: form.cert_document_provided,
        cert_document_current: form.cert_document_current,
        discount_type: form.discount_type || null,
        discount_value: parseFloat(form.discount_value || "0"),
        taxable_charges: parseFloat(form.taxable_charges || "0"),
        non_taxable_charges: parseFloat(form.non_taxable_charges || "0"),
        quantity: parseInt(form.quantity || "1", 10) || 1,
      };
      return (await apiClient.post("/api/v1/tax-schemes/simulate", payload))
        .data as SimulationResult;
    },
    onSuccess: (data) => {
      setResult(data);
      toast.success(
        "Simulation complete",
        `Total tax: ${fmtCurrency(data.total_tax)}`,
      );
    },
    onError: () => toast.error("Simulation failed"),
  });

  const selectClass =
    "mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)] transition-all duration-200 hover:border-[var(--border-strong)] focus:outline-none focus-visible:shadow-[var(--focus-ring)] focus-visible:border-[var(--border-focus)]";

  const matchedCount = result
    ? result.evaluation_trace.filter((t) => t.matched).length
    : 0;
  const totalEvaluated = result ? result.evaluation_trace.length : 0;

  return (
    <ActionDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Tax Simulator"
      description="Test how your scheme rules apply to different scenarios"
      icon={Calculator}
      size="lg"
    >
      <div className="space-y-4">
        {/* Row 1: Order type + Subtotal */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[13px] font-medium text-[var(--text-primary)] mb-1.5">
              Order Type
            </label>
            <select
              value={form.entity_type}
              onChange={(e) => setSimField("entity_type", e.target.value)}
              className={selectClass}
            >
              <option value="sales_order">Sales Order</option>
              <option value="purchase_order">Purchase Order</option>
            </select>
          </div>
          <Input
            label="Subtotal ($)"
            type="number"
            step="0.01"
            min="0"
            value={form.subtotal}
            onChange={(e) => setSimField("subtotal", e.target.value)}
            placeholder="10000.00"
          />
        </div>

        {/*
          Row 1b: the rest of what moves the total.

          A simulator that only takes a subtotal can only ever explain the tax on
          a subtotal — and a real order has a discount, charges that may or may
          not be taxable, and a quantity that a per-unit fee multiplies by. Tax on
          the wrong base is the thing this screen exists to catch.
        */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-[13px] font-medium text-[var(--text-primary)] mb-1.5">
              Discount
            </label>
            <select
              value={form.discount_type}
              onChange={(e) =>
                setSimField(
                  "discount_type",
                  e.target.value as "" | "percent" | "amount",
                )
              }
              className={selectClass}
            >
              <option value="">None</option>
              <option value="percent">Percentage</option>
              <option value="amount">Fixed amount</option>
            </select>
          </div>
          {form.discount_type ? (
            <Input
              label={
                form.discount_type === "percent"
                  ? "Discount (%)"
                  : "Discount ($)"
              }
              type="number"
              step="0.01"
              min="0"
              value={form.discount_value}
              onChange={(e) => setSimField("discount_value", e.target.value)}
              placeholder="0.00"
            />
          ) : (
            <div />
          )}
          <Input
            label="Quantity"
            type="number"
            min="1"
            step="1"
            value={form.quantity}
            onChange={(e) => setSimField("quantity", e.target.value)}
            placeholder="1"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Taxable charges ($)"
            type="number"
            step="0.01"
            min="0"
            value={form.taxable_charges}
            onChange={(e) => setSimField("taxable_charges", e.target.value)}
            placeholder="0.00"
          />
          <Input
            label="Non-taxable charges ($)"
            type="number"
            step="0.01"
            min="0"
            value={form.non_taxable_charges}
            onChange={(e) => setSimField("non_taxable_charges", e.target.value)}
            placeholder="0.00"
          />
        </div>

        {/* Row 2: Customer state */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[13px] font-medium text-[var(--text-primary)] mb-1.5">
              Customer State
            </label>
            <select
              value={form.customer_state}
              onChange={(e) => setSimField("customer_state", e.target.value)}
              className={selectClass}
            >
              <option value="">— None —</option>
              {US_STATES.map((st) => (
                <option key={st} value={st}>
                  {st}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-[var(--text-tertiary)] mt-1">
              Compared against your org state for interstate detection
            </p>
          </div>
          <MultiSelect
            label="Customer Tags"
            options={tagOptions}
            value={form.customer_tags}
            onChange={(vals) => setSimField("customer_tags", vals)}
            placeholder="Select tags..."
            creatable
          />
        </div>

        {/* Row 3: Asset categories */}
        <MultiSelect
          label="Asset Categories"
          options={categoryOptions}
          value={form.asset_categories}
          onChange={(vals) => setSimField("asset_categories", vals)}
          placeholder="Select categories..."
          creatable
        />

        {/* Row 4: Certificate simulation toggles */}
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 select-none">
            <input
              type="checkbox"
              checked={form.cert_document_provided}
              onChange={(e) =>
                setSimField("cert_document_provided", e.target.checked)
              }
              className="h-4 w-4 accent-[var(--color-interactive-primary)]"
            />
            <span className="text-sm text-[var(--text-primary)]">
              Document Provided
            </span>
          </label>
          <label
            className={`flex items-center gap-2 select-none transition-opacity ${
              form.cert_document_provided
                ? "opacity-100"
                : "opacity-40 pointer-events-none"
            }`}
          >
            <input
              type="checkbox"
              checked={form.cert_document_current}
              onChange={(e) =>
                setSimField("cert_document_current", e.target.checked)
              }
              disabled={!form.cert_document_provided}
              className="h-4 w-4 accent-[var(--color-interactive-primary)]"
            />
            <span className="text-sm text-[var(--text-primary)]">
              Document Current
            </span>
          </label>
        </div>

        {/* Simulate button */}
        <div className="flex items-center gap-3 pt-1">
          <Button
            onClick={() => simulateMutation.mutate()}
            isLoading={simulateMutation.isPending}
          >
            <Play className="h-3.5 w-3.5" /> Simulate
          </Button>
          {result && (
            <button
              type="button"
              onClick={() => setResult(null)}
              className="text-xs text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
            >
              Clear results
            </button>
          )}
        </div>

        {/* ── Results ── */}
        {result && (
          <div className="space-y-3 border-t border-[var(--border-subtle)] pt-4">
            {/* Context bar */}
            <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--text-secondary)]">
              {result.context.org_state && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--surface-sunken)]">
                  Org: {result.context.org_state}
                </span>
              )}
              {result.context.customer_state && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--surface-sunken)]">
                  Customer: {result.context.customer_state}
                </span>
              )}
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${
                  result.context.is_interstate
                    ? "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300"
                    : "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                }`}
              >
                {result.context.is_interstate ? "Interstate" : "Same State"}
              </span>
              {result.context.counterparty_tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300"
                >
                  <Tag className="h-3 w-3" /> {t}
                </span>
              ))}
              {result.context.is_tax_exempt && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                  Tax Exempt
                </span>
              )}
            </div>

            {/*
              Totals.

              The base decomposition is the answer to "why is the tax that
              number" — without it the screen quotes a figure and explains
              nothing. Fees are shown separately from tax because they are
              different money: a delivery charge folded into a tax line is wrong,
              and an accountant will say so.
            */}
            <div className="space-y-1.5 bg-[var(--surface-sunken)] rounded-[var(--radius-md)] px-4 py-3">
              {result.base && (
                <>
                  <SimTotalRow label="Subtotal" value={result.base.subtotal} />
                  {result.base.discount_amount > 0 && (
                    <SimTotalRow
                      label="Discount"
                      value={-result.base.discount_amount}
                    />
                  )}
                  {result.base.taxable_charges > 0 && (
                    <SimTotalRow
                      label="Charges (taxable)"
                      value={result.base.taxable_charges}
                    />
                  )}
                  {result.base.non_taxable_charges > 0 && (
                    <SimTotalRow
                      label="Charges (not taxable)"
                      value={result.base.non_taxable_charges}
                    />
                  )}
                  {result.total_fees > 0 && (
                    <SimTotalRow label="Fees" value={result.total_fees} />
                  )}
                  <SimTotalRow
                    label="Taxed on"
                    value={result.base.taxable_base}
                    muted
                  />
                </>
              )}
              <div className="flex items-center justify-between border-t border-[var(--border-default)] pt-1.5">
                <span className="text-sm font-medium text-[var(--text-primary)]">
                  Tax
                </span>
                <span className="text-sm font-semibold tabular-nums text-[var(--text-primary)]">
                  {fmtCurrency(result.total_tax)}
                </span>
              </div>
              {result.grand_total > 0 && (
                <div className="flex items-center justify-between border-t border-[var(--border-default)] pt-1.5">
                  <span className="text-sm font-semibold text-[var(--text-primary)]">
                    Order total
                  </span>
                  <span className="text-lg font-bold tabular-nums text-[var(--text-primary)]">
                    {fmtCurrency(result.grand_total)}
                  </span>
                </div>
              )}
            </div>

            {/* Warnings */}
            {result.warnings.length > 0 && (
              <div className="space-y-1">
                {result.warnings.map((w, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-300"
                  >
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>{w}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Evaluation trace */}
            <div className="rounded-[var(--radius-md)] border border-[var(--border-subtle)] overflow-hidden">
              <div className="bg-[var(--surface-sunken)] px-4 py-2 border-b border-[var(--border-subtle)]">
                <span className="text-xs font-medium text-[var(--text-secondary)]">
                  {totalEvaluated} scheme{totalEvaluated !== 1 ? "s" : ""}{" "}
                  evaluated · {matchedCount} applied
                </span>
              </div>
              <div className="divide-y divide-[var(--border-subtle)]">
                {result.evaluation_trace.map((t, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center gap-3 px-4 py-2.5 ${
                      t.matched ? "bg-green-50/50 dark:bg-green-900/5" : ""
                    }`}
                  >
                    {t.matched ? (
                      <Check className="h-4 w-4 text-green-600 dark:text-green-300 shrink-0" />
                    ) : (
                      <X className="h-4 w-4 text-red-400 dark:text-red-500 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[var(--text-primary)] truncate">
                          {t.scheme_name}
                        </span>
                        <span className="text-xs text-[var(--text-tertiary)]">
                          {t.scheme_type === "percentage" && t.rate != null
                            ? `${(Number(t.rate) * 100).toFixed(2)}%`
                            : t.flat_amount != null
                              ? fmtCurrency(Number(t.flat_amount))
                              : ""}
                        </span>
                        <span className="text-[10px] text-[var(--text-tertiary)]">
                          P{t.priority}
                        </span>
                        <span className="rounded-full bg-[var(--surface-sunken)] px-1.5 py-0 text-[10px] font-medium text-[var(--text-tertiary)]">
                          {(t.apply_level ?? "order") === "line"
                            ? "Fee"
                            : "Tax"}
                        </span>
                      </div>
                      {t.skip_reason && (
                        <p className="text-[11px] text-[var(--text-tertiary)] mt-0.5 truncate">
                          {t.skip_reason}
                        </p>
                      )}
                    </div>
                    {t.matched && t.applied_amount != null && (
                      <span className="text-sm font-bold text-green-700 dark:text-green-300 shrink-0">
                        {fmtCurrency(Number(t.applied_amount))}
                      </span>
                    )}
                  </div>
                ))}
                {result.evaluation_trace.length === 0 && (
                  <div className="px-4 py-6 text-center text-xs text-[var(--text-tertiary)]">
                    No active schemes found for this order type.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </ActionDrawer>
  );
}

export default TaxSchemesPage;
